import React, { useState, useEffect } from "react";
import { Flex, Text, Input, Box, Checkbox, Button } from "@chakra-ui/react";
import DynamicSelectInput from "./DynamicSelect";

const BusinessItemsView = ({
    urlOptions,
    colorMain,
    setSelectionDetails,
    setSelection,
    setAmount,
    amount,
    setTotalSplitAmountB,
    setIsSelectButtonDisabled,
    setAnyChecked,
    currencyText = "KES",
    urlOption,
    isDisabled,
}) => {
    const [items, setItems] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        if (urlOptions && urlOptions.length > 0) {
            setItems(
                urlOptions
                    .filter((opt) => opt.value?.trim())
                    .map((opt) => ({
                        name: opt.value,
                        price: opt.target || 0,
                        quantity: 1,
                        subCategories: (opt.subCategories || "")
                            .split(",")
                            .map((sub) => sub.trim())
                            .filter(Boolean),
                        subCategory: "",
                        isChecked: opt.value === urlOption,
                    }))
            );
        }
    }, [urlOptions, urlOption]);

    const handleCheckboxChange = (index) => {
        if (isDisabled) return;
        setItems((prevItems) => {
            const newItems = [...prevItems];
            newItems[index] = {
                ...newItems[index],
                isChecked: !newItems[index].isChecked,
            };
            return newItems;
        });
    };

    const handleQuantityChange = (index, delta) => {
        setItems((prevItems) => {
            const newItems = [...prevItems];
            const item = newItems[index];
            const newQuantity = Math.max(1, item.quantity + delta);
            newItems[index] = { ...item, quantity: newQuantity };
            return newItems;
        });
    };

    const handleQuantityInputChange = (e, index) => {
        const val = parseInt(e.target.value, 10);
        if (!isNaN(val) && val >= 1) {
            setItems((prevItems) => {
                const newItems = [...prevItems];
                newItems[index] = { ...newItems[index], quantity: val };
                return newItems;
            });
        }
    };

    useEffect(() => {
        const selectedItems = items
            .filter((item) => item.isChecked)
            .map((item) => ({
                ...item,
                name: item.subCategory ? `${item.name}#${item.subCategory}` : item.name,
            }));
        const missingSubCategory = items.some(
            (item) => item.isChecked && item.subCategories.length > 0 && !item.subCategory
        );
        const total = selectedItems.reduce(
            (sum, item) => sum + (item.price > 0 ? (item.price * item.quantity) : parseFloat(item.customPrice || 0)),
            0
        );

        setAmount(total > 0 ? total : "");
        setTotalSplitAmountB && setTotalSplitAmountB(total);

        if (selectedItems.length > 0) {
            if (setSelectionDetails) {
                setSelectionDetails(selectedItems.map(item => ({
                    name: item.name,
                    amount: item.price > 0 ? item.price * item.quantity : parseFloat(item.customPrice || 0),
                    quantity: item.price > 0 ? item.quantity : 1,
                    unitPrice: item.price > 0 ? item.price : parseFloat(item.customPrice || 0),
                    isChecked: true,
                    isEditable: item.price <= 0
                })));
            }
            if (setSelection) {
                const selectionString = selectedItems.map(item =>
                    item.price > 0
                        ? `${item.name} (x${item.quantity})`
                        : `${item.name} (KES ${item.customPrice || 0})`
                ).join(", ");
                setSelection(selectionString);
            }
            setIsSelectButtonDisabled && setIsSelectButtonDisabled(missingSubCategory);
            setAnyChecked && setAnyChecked(true);
        } else {
            if (setSelectionDetails) setSelectionDetails([]);
            if (setSelection) setSelection("");
            setIsSelectButtonDisabled && setIsSelectButtonDisabled(true);
            setAnyChecked && setAnyChecked(false);
        }
    }, [items, setAmount, setSelectionDetails, setSelection, setIsSelectButtonDisabled, setAnyChecked, setTotalSplitAmountB]);

    return (
        <Box w="100%">
            {items.length > 7 && (
                <Box mb={4} px={1}>
                    <Input
                        placeholder="Search items..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        bg="white"
                        variant="filled"
                        borderRadius="md"
                    />
                </Box>
            )}

            <Box
                maxH={items.length > 7 ? "60vh" : "unset"}
                overflowY={items.length > 7 ? "auto" : "visible"}
                px={1}
                css={{
                    '&::-webkit-scrollbar': { width: '4px' },
                    '&::-webkit-scrollbar-track': { width: '6px' },
                    '&::-webkit-scrollbar-thumb': { background: 'gray', borderRadius: '24px' },
                }}
            >
                {items
                    .map((item, index) => ({ ...item, originalIndex: index }))
                    .filter((item) =>
                        (item.name || "").toLowerCase().includes(searchQuery.toLowerCase())
                    )
                    .map((item) => {
                        const index = item.originalIndex;
                        return (
                            <Box
                                key={index}
                                w="100%"
                                bg="gray.50"
                                p={6}
                                borderRadius="xl"
                                mb={4}
                                transition="all 0.3s"
                            >
                                <Flex
                                    alignItems="center"
                                    justifyContent="space-between"
                                    cursor="pointer"
                                    onClick={() => handleCheckboxChange(index)}
                                >
                                    <Flex alignItems="center">
                                        <Checkbox
                                            isChecked={item.isChecked}
                                            onChange={(e) => {
                                                e.stopPropagation();
                                                handleCheckboxChange(index);
                                            }}
                                            colorScheme="teal"
                                            size="lg"
                                            mr={4}
                                            borderColor="gray.400"
                                            isDisabled={isDisabled}
                                        />
                                        <Flex flexDir="column">
                                            <Text fontWeight="bold" fontSize="lg" color="gray.700">
                                                {item.name}
                                            </Text>
                                            {item.price > 0 && (
                                                <Text fontSize="sm" color="gray.500" fontWeight="medium">
                                                    KES {item.price.toLocaleString()}
                                                </Text>
                                            )}
                                        </Flex>
                                    </Flex>
                                </Flex>

                                {item.isChecked && (
                                    <Box mt={6} pt={4} borderTop="1px solid" borderColor="gray.200">
                                        {item.subCategories.length > 0 && (
                                            <Box mb={4}>
                                                <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={2} textTransform="uppercase">
                                                    Subcategory
                                                </Text>
                                                <DynamicSelectInput
                                                    options={item.subCategories.map((sub) => ({ value: sub }))}
                                                    value={item.subCategory}
                                                    placeholder="Select a subcategory"
                                                    isDisabled={isDisabled}
                                                    onChange={(e) =>
                                                        setItems((prev) => {
                                                            const next = [...prev];
                                                            next[index] = { ...next[index], subCategory: e.target.value };
                                                            return next;
                                                        })
                                                    }
                                                />
                                            </Box>
                                        )}
                                        <Flex w="100%" justifyContent="space-between" alignItems="flex-end">
                                            {item.price > 0 ? (
                                                <>
                                                    <Flex flexDir="column">
                                                        <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1} textTransform="uppercase">
                                                            Price
                                                        </Text>
                                                        <Text fontSize="2xl" fontWeight="bold" color={colorMain} lineHeight="1">
                                                            {(item.price * item.quantity).toLocaleString()}
                                                        </Text>
                                                    </Flex>

                                                    <Flex flexDir="column" alignItems="flex-end">
                                                        <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={2} textTransform="uppercase">
                                                            Quantity
                                                        </Text>
                                                        <Flex
                                                            alignItems="center"
                                                            border="1px solid"
                                                            borderColor="gray.200"
                                                            borderRadius="lg"
                                                            bg="white"
                                                            overflow="hidden"
                                                            h="40px"
                                                        >
                                                            <Button
                                                                size="sm"
                                                                onClick={() => handleQuantityChange(index, -1)}
                                                                variant="ghost"
                                                                borderRadius="none"
                                                                _hover={{ bg: "gray.50" }}
                                                                h="100%"
                                                                w="40px"
                                                                fontSize="xl"
                                                                fontWeight="medium"
                                                                color="gray.600"
                                                                isDisabled={isDisabled || item.quantity <= 1}
                                                            >
                                                                -
                                                            </Button>
                                                            <Input
                                                                type="number"
                                                                variant="unstyled"
                                                                value={item.quantity}
                                                                onChange={(e) => handleQuantityInputChange(e, index)}
                                                                min={1}
                                                                textAlign="center"
                                                                w="50px"
                                                                fontSize="lg"
                                                                fontWeight="semibold"
                                                                color={colorMain}
                                                                isReadOnly={isDisabled}
                                                            />
                                                            <Button
                                                                size="sm"
                                                                onClick={() => handleQuantityChange(index, 1)}
                                                                variant="ghost"
                                                                borderRadius="none"
                                                                _hover={{ bg: "gray.50" }}
                                                                h="100%"
                                                                w="40px"
                                                                fontSize="xl"
                                                                fontWeight="medium"
                                                                color="gray.600"
                                                                isDisabled={isDisabled}
                                                            >
                                                                +
                                                            </Button>
                                                        </Flex>
                                                    </Flex>
                                                </>
                                            ) : (
                                                <Flex flexDir="column" w="100%">
                                                    <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={2} textTransform="uppercase">
                                                        Amount
                                                    </Text>
                                                    <Input
                                                        type="number"
                                                        placeholder="Enter Amount"
                                                        value={item.customPrice || ""}
                                                        onChange={(e) => {
                                                            const val = e.target.value;
                                                            setItems((prevItems) => {
                                                                const newItems = [...prevItems];
                                                                newItems[index] = { ...newItems[index], customPrice: val };
                                                                return newItems;
                                                            });
                                                        }}
                                                        bg="white"
                                                        size="lg"
                                                        borderColor="gray.300"
                                                        _focus={{ borderColor: colorMain }}
                                                        onClick={(e) => e.stopPropagation()}
                                                        isReadOnly={false}
                                                    />
                                                </Flex>
                                            )}
                                        </Flex>
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
            </Box>

            {amount > 0 && (
                <Flex
                    w="100%"
                    justifyContent="space-between"
                    alignItems="center"
                    p={4}
                    bg="gray.50"
                    borderRadius="xl"
                    mt={4}
                >
                    <Text fontSize="lg" fontWeight="bold" color="gray.700">
                        Total Amount
                    </Text>
                    <Text fontSize="2xl" fontWeight="bold" color={colorMain}>
                        {currencyText} {Number(amount).toLocaleString()}
                    </Text>
                </Flex>
            )}
        </Box>
    );
};

export default BusinessItemsView;
